import React, { useEffect, useState } from "react";
import PageHero from "@/components/UI/PageHero";
import yobstiLaden from "@/public/img/yobsti-laden.png";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import StoreCard from "@/components/UI/StoreCard";
import { Store } from "@/models/customersTypes";
import { showToast } from "@/components/UI/Toast";
import mapMarker from "@/public/svg/map-marker-pink.svg";
import mapMarkerGreen from "@/public/svg/map-marker-green.svg";
import { comingSoonStores } from "@/data/comingSoonStores";
import LoadingIcon from "@/components/UI/LoadingIcon";
import SeoHelmet from "@/components/UI/SeoHelmet";
import seoImg from "@/public/img/seo/standorte.png";
import { track } from '@/helpers/analytics';

void React;

delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-icon-2x.png',
    iconUrl: mapMarker,
    shadowUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png',
});

const comingSoonIcon = L.icon({
    iconUrl: mapMarkerGreen,
    shadowUrl: 'https://unpkg.com/leaflet@1.9.3/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

export default function LocationsPage() {
    const [allStores, setAllStores] = useState<Store[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const openStores =
        [
            {
                id: 1,
                name: "Knittelsheim",
                street: "Hauptstraße 18",
                city: "Knittelsheim",
                zip: "76879",
                latitude: 49.192,
                longitude: 8.251,
                openingHours: "Täglich 6–22 Uhr geöffnet"
            },
            {
                id: 2,
                name: "Völkersweiler",
                street: "Hauptstraße 25a",
                city: "Völkersweiler",
                zip: "76857",
                latitude: 49.1695,
                longitude: 7.9319,
                openingHours: "Täglich 6–22 Uhr geöffnet"
            },
            {
                id: 3,
                name: "Schwanheim",
                street: "Hubertusstraße 1",
                city: "Schwanheim",
                zip: "76848",
                latitude: 49.1671,
                longitude: 7.8772,
                openingHours: "Täglich 6–22 Uhr geöffnet"
            },
            {
                id: 4,
                name: "Huttenheim",
                street: "Rheinstraße 28",
                city: "Philippsburg - Huttenheim",
                zip: "76661",
                latitude: 49.1982,
                longitude: 8.4676,
                openingHours: "Täglich 6–22 Uhr geöffnet"
            },
        ];

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        try {
            setAllStores(openStores);
        } catch {
            showToast('Die Märkte konnten nicht geladen werden.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) return (<LoadingIcon text="Lade Produkte ..." />);

    return (
        <>
            <SeoHelmet
                title="Yobsti Standorte – Unsere Dorfläden in Ihrer Nähe"
                description="Entdecken Sie alle Yobsti Standorte: Moderne Dorfläden für Nahversorgung ohne Personal."
                image={seoImg}
                url="https://www.yobsti.de/standorte"
            />

            <PageHero eyebrow="frisch. lokal. smart." title="Wo ihr uns finden könnt!" text="39 Standorte, auch bei dir in der Nähe." illustration={yobstiLaden} />

            <section className="max-w-6xl mx-auto px-4 py-12">
                <div className="flex h-[500px] justify-center rounded-xl overflow-hidden">
                    <MapContainer
                        bounds={L.latLngBounds(
                            [...allStores, ...comingSoonStores].map((s) => [s.latitude, s.longitude])
                        )}
                        boundsOptions={{ padding: [20, 20] }}
                        zoomSnap={0.25}
                        scrollWheelZoom={false}
                        style={{ height: '100%', width: '80%' }}
                    >
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
                        />
                        {allStores.map((store, idx) => (
                            <Marker
                                key={idx}
                                position={[store.latitude, store.longitude]}
                                eventHandlers={{ click: () => track('store-select', { store: store.name, coming_soon: false }) }}
                            >
                                <Popup>{store.name}</Popup>
                            </Marker>
                        ))}
                        {comingSoonStores.map((store) => (
                            <Marker
                                key={store.id}
                                position={[store.latitude, store.longitude]}
                                icon={comingSoonIcon}
                                eventHandlers={{ click: () => track('store-select', { store: store.name, coming_soon: true }) }}
                            >
                                <Popup>{store.name} – bald für dich da</Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </div>

                <h2 className="text-2xl font-bold mt-12">Bereits geöffnete Märkte</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    {openStores.map((store) => (
                        <StoreCard key={store.id} store={store} />
                    ))}
                </div>

                <h2 id="bald-fuer-dich-da" className="text-2xl font-bold mt-12 scroll-mt-4">Bald für dich da</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4" data-testid="coming-soon-stores">
                    {comingSoonStores.map((store) => (
                        <StoreCard key={store.id} store={store} comingSoon />
                    ))}
                </div>
            </section>
        </>
    );
}
